import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormInputState, FormModel, ResultValue } from 'src_common/common/mobx-utils/Form';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import { CustomSelectionType, FieldType } from './commonCmsGuard';
import { UploadImageState } from 'src/AppState/UploadImageState/UploadImageState';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { SelectionModel } from 'src_common/common/websocket2/models/SelectionModel/SelectionModel';
import {
    CustomSelectionsTemplateState,
    SingleFieldType,
} from 'src/AppState/CmsState/EventDetailsState/CustomSelectionsTemplate/CustomSelectionsTemplateState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { FieldState } from 'src/AppState/CmsState/EventDetailsState/FieldState';
import { CustomSelectionItemState } from './CustomSelectionsTemplate/CustomSelectionItemState';
import { validateExpectHexColor, validateExpectNumber } from 'src/api/utils/formValidators';
import { InputStatus } from 'src/components/Input/InputStatus';
import { TypeId } from 'src_common/common/websocket2/type';

export type FormStatus = 'ok' | 'half-ok' | 'error' | 'not-checked';

export class CustomSelectionStateNew {
    @observable isOpenAccordion: boolean = false;

    readonly descriptionModel: FormModel<string>;
    readonly description: FormInputState<string>;
    readonly descriptionStatus: TextareaStatus;

    readonly starValue: FormInputState<string>;
    readonly starValueModel: FormModel<number | null>;
    readonly starValueStatus: InputStatus;

    readonly colorFieldModel: FormModel<string>;
    readonly colorField: FormInputState<string>;
    readonly colorFieldStatus: InputStatus;

    public imageField: UploadImageState;
    private readonly fieldItemStateMap: MobxMapAutoNew<string, FieldState>;

    constructor(
        private readonly modelsState: ModelsState,
        private readonly customSelectionsTemplateState: CustomSelectionsTemplateState,
        private readonly marketId: TypeId.MarketId,
        private readonly selectionId: number,
        private readonly selectionTemplate: string
    ) {
        this.fieldItemStateMap = new MobxMapAutoNew((_slug: string): FieldState => new FieldState());

        this.description = new FormInputState('', false);
        this.descriptionModel = this.description.toModel();
        this.descriptionStatus = new TextareaStatus(this.descriptionModel);

        this.starValue = new FormInputState('', false);
        this.starValueModel = this.starValue.map(validateExpectNumber);
        this.starValueStatus = new InputStatus(this.starValueModel);

        this.colorField = new FormInputState('', false);
        this.colorFieldModel = this.colorField.map(validateExpectHexColor);
        this.colorFieldStatus = new InputStatus(this.colorFieldModel);

        this.imageField = new UploadImageState('custom-selection/image-field');
        makeAutoObservable(this);
    }

    getFieldBySlug = (slug: string): FieldState => {
        return this.fieldItemStateMap.get(slug);
    };

    @computed private get selectionItem(): CustomSelectionItemState {
        return this.customSelectionsTemplateState.getSelectionItemStateBySlug(this.selectionTemplate);
    }

    @computed public get fields(): Array<SingleFieldType> {
        return this.selectionItem.fields;
    }

    @computed public get isColor(): boolean {
        return this.selectionItem.isColor;
    }

    @computed public get isDescription(): boolean {
        return this.selectionItem.isDescription;
    }

    @computed public get isImage(): boolean {
        return this.selectionItem.isImage;
    }

    @computed public get isRating(): boolean {
        return this.selectionItem.isRating;
    }

    @computed public get selectionTemplateForView(): string {
        return this.selectionTemplate;
    }

    @computed public get selectionData(): CustomSelectionType {
        const basicFields: Array<FieldType> = [];
        for (const field of this.fields) {
            const fieldItemValue = this.fieldItemStateMap.get(field.slug).value;
            basicFields.push({
                value: fieldItemValue,
                label: field.label,
                slug: field.slug,
            });
        }

        const additionalFields: CustomSelectionType = {
            id: this.selectionId,
            selection_type: this.selectionTemplate,
            star_value: this.starFieldValue,
            image: this.imageValue,
            description: this.descriptionValue,
            color: this.colorValue,
            fields: basicFields,
        };

        return additionalFields;
    }

    @computed public get starFieldValue(): number {
        if (this.starValueModel.result instanceof ResultValue) {
            return this.starValueModel.result.value ?? 0;
        }

        return 0;
    }

    @computed public get imageValue(): string {
        return this.imageField.imgUrl ?? '';
    }

    @computed public get descriptionValue(): string {
        if (this.descriptionModel.result instanceof ResultValue) {
            return this.descriptionModel.result.value ?? '';
        }

        return '';
    }

    @computed public get colorValue(): string {
        if (this.colorFieldModel.result instanceof ResultValue) {
            return this.colorFieldModel.result.value ?? '';
        }

        return '';
    }

    @computed private get selectionModel(): SelectionModel | null {
        return this.modelsState.getSelectionAndLoad(this.marketId, this.selectionId);
    }

    @computed public get selectionName(): string {
        return this.selectionModel?.name ?? '';
    }

    @computed public get isValidForm(): boolean {
        return true;
    }

    public onToggleAccordion = (): void => {
        this.isOpenAccordion = !this.isOpenAccordion;
    };

    @action public setSelectionForm = (selection: CustomSelectionType): void => {
        this.description.setValue(selection.description);
        this.starValue.setValue(selection.star_value === 0 ? '' : selection.star_value.toString());
        this.colorField.setValue(selection.color);
        this.imageField.handleChooseImage(selection.image);
        for (const field of selection.fields) {
            this.getFieldBySlug(field.slug).field.setValue(field.value);
        }
    };

    public get starValueOptions() {
        return [
            {
                key: '',
                name: 'not set',
            },
            {
                key: '1',
                name: '1',
            },
            {
                key: '2',
                name: '2',
            },
            {
                key: '3',
                name: '3',
            },
            {
                key: '4',
                name: '4',
            },
            {
                key: '5',
                name: '5',
            },
        ];
    }
}
